import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { formatDistanceToNow, parseISO } from "date-fns";
import "react-calendar/dist/Calendar.css";
import Sidebar from "../components/Sidebar";
import { toast } from "react-toastify";
import {
  NotificationPreviewModal,
  PaymentApproveModal,
  PaymentDeclineModal,
  PasswordVerificationModal,
  NotificationSuccessModal,
} from "../components/modals/notificationModal";
import { getAuthToken, getAdminName } from "../utils/cookieUtils";
import { useLocations } from "../hooks/useLocations";

function Notifications() {
  // Location selection state
  const { locations, loading: locationsLoading, currentAdminRoleId, currentAdminLocationId } = useLocations(true);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  // Handle location selection
  const handleLocationChange = (id) => {
    setSelectedLocations((prev) =>
      prev.includes(id)
        ? prev.filter((locId) => locId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAllLocations = () => {
    if (selectedLocations.length === locations.length) {
      setSelectedLocations([]);
    } else {
      setSelectedLocations(locations.map((loc) => loc.id));
    }
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.location-dropdown-container')) {
        setShowLocationDropdown(false);
      }
    };
    if (showLocationDropdown) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showLocationDropdown]);
  const adminData = getAdminName();
  const [notifications, setNotifications] = useState([]),
    [searchTerm, setSearchTerm] = useState(""),
    [searchQuery, setSearchQuery] = useState(""),
    [notifApprove, setNotifApprove] = useState(false),
    [notifDecline, setNotifDecline] = useState(false),
    [viewModal, setViewModal] = useState(false),
    [viewModal2, setViewModal2] = useState(false),
    [approveSuccess, setApproveSuccess] = useState(false),
    [declineSuccess, setDeclineSuccess] = useState(false),
    [notifPreview, setNotifPreview] = useState(false),
    [sendNotifSuccess, setSendNotifSuccess] = useState(false),
    [currentTnxRef, setCurrentTnxRef] = useState(null),
    [currentId, setCurrentId] = useState(null),
    [paymentDetails, setPaymentDetails] = useState(null),
    [password, setPassword] = useState(""),
    [messageHeader, setMessageHeader] = useState(""),
    [message, setMessage] = useState(""),
    [notificationLocationId, setNotificationLocationId] = useState(""),
    [loading, setLoading] = useState(false);

  // If the admin can only see one location (branch admin or admin), preselect it
  useEffect(() => {
    // Prefer the admin's profile location id if available
    if ((currentAdminRoleId === 4 || currentAdminRoleId === 2) && currentAdminLocationId) {
      setNotificationLocationId(String(currentAdminLocationId));
      return;
    }

    if (!locationsLoading && locations.length === 1 && (currentAdminRoleId === 4 || currentAdminRoleId === 2)) {
      setNotificationLocationId(String(locations[0].id));
    }
  }, [locations, locationsLoading, currentAdminRoleId, currentAdminLocationId]);

  // Function to refresh notifications
  const refreshNotifications = () => {
    const token = getAuthToken();
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        // Build query parameters for server-side pagination
        const params = new URLSearchParams({
          page: currentPage.toString(),
          per_page: '10'
        });
        
        // Add search query if exists
        if (searchQuery.trim()) {
          params.append('search', searchQuery);
        }
        
        const response = await axios.get(
          `/super-admin/get-app-notifications?${params.toString()}`,
          config
        );
        
        // Handle paginated response from server
        if (response.data.message.notifications) {
          const paginationData = response.data.message.notifications;
          
          // Set notifications from the data array
          setNotifications(paginationData.data || []);
          
          // Set pagination metadata
          setTotalPages(paginationData.last_page || 1);
          setTotalNotifications(paginationData.total || 0);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
        toast.error("Failed to fetch notifications. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);

  const [fileSize, setFileSize] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const currentDate = useMemo(() => new Date(), []);
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthsOfYear = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );
  const dayOfWeekIndex = currentDate.getDay(),
    monthIndex = currentDate.getMonth(),
    dayOfMonth = currentDate.getDate();
  const formattedDate = `${daysOfWeek[dayOfWeekIndex]}, ${monthsOfYear[monthIndex]} ${dayOfMonth}`;

  useEffect(() => {
    const token = getAuthToken();
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        // Build query parameters for server-side pagination
        const params = new URLSearchParams({
          page: currentPage.toString(),
          per_page: '10'
        });
        
        // Add search query if exists
        if (searchQuery.trim()) {
          params.append('search', searchQuery);
        }
        
        const response = await axios.get(
          `/super-admin/get-app-notifications?${params.toString()}`,
          config
        );
        
        // Handle paginated response from server
        if (response.data.message.notifications) {
          const paginationData = response.data.message.notifications;
          
          // Set notifications from the data array
          setNotifications(paginationData.data || []);
          
          // Set pagination metadata
          setTotalPages(paginationData.last_page || 1);
          setTotalNotifications(paginationData.total || 0);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
        toast.error("Failed to fetch notifications. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [currentPage, searchQuery]); // Add dependencies for server-side pagination

  const displayNotifications = notifications;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value === "") {
      setSearchQuery("");
      setCurrentPage(1);
    }
  };
  
  const handleSearchClick = () => {
    setSearchQuery(searchTerm);
    setCurrentPage(1); 
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    if (notifications.length === 0 && searchQuery && !loading) {
      const timeoutId = setTimeout(() => {
        setSearchQuery("");
        setSearchTerm("");
        setCurrentPage(1);
      }, 3000); 

      return () => clearTimeout(timeoutId);
    }
  }, [notifications, searchQuery, loading]);

  const fetchPaymentDetails = async (tnxRef) => {
    const token = getAuthToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const response = await axios.post(
        "/super-admin/get-subscription-payment-detail",
        { tnx_ref: tnxRef },
        config
      );
      setPaymentDetails(response.data.message);
    } catch (error) {
      console.error("Error fetching payment details:", error);
      toast.error("Failed to load payment details. Please try again.");
    }
  };

  const notifPreviewOpen = () => {
    setNotifPreview(true);
  };

  const notifApproveOpen = async (tnxRef, id) => {
    setCurrentTnxRef(tnxRef);
    setCurrentId(id);
    await fetchPaymentDetails(tnxRef);
    setNotifApprove(true);
  };

  const notifDeclineOpen = async (tnxRef, id) => {
    setCurrentTnxRef(tnxRef);
    setCurrentId(id);
    await fetchPaymentDetails(tnxRef);
    setNotifDecline(true);
  };

  const handleApprovePayment = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const response = await axios.post(
        "/super-admin/approve-subscription-payment",
        { tnx_ref: currentTnxRef, password, notification_id: currentId },
        config
      );
      setApproveSuccess(true);
      setViewModal(false);
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.type === "error"
      ) {
        toast.error(error.response.data.message);
        setViewModal(false);
      } else {
        console.error("Error approving payment:", error);
        let errorMessage = "Failed to approve payment. Please try again.";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = `Network error: ${error.message}`;
        }
        toast.error(errorMessage);
        setViewModal(false);
      }
    }
  };

  const handleDeclinePayment = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const response = await axios.post(
        "/super-admin/decline-subscription-payment",
        { tnx_ref: currentTnxRef, password, notification_id: currentId },
        config
      );
      setDeclineSuccess(true);
      setViewModal2(false);
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.type === "error"
      ) {
        toast.error(error.response.data.message);
        setViewModal2(false);
      } else {
        console.error("Error declining payment:", error);
        let errorMessage = "Failed to decline payment. Please try again.";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = `Network error: ${error.message}`;
        }
        toast.error(errorMessage);
        setViewModal2(false);
      }
    }
  };

  const validateAndSetImage = (file) => {
    const validTypes = ["image/jpeg", "image/png"];
    const maxSize = 4 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPEG and PNG are allowed.");
      return;
    }

    if (file.size > maxSize) {
      toast.error("File size exceeds 4mb. Please upload a smaller file.");
      return;
    }

    const fileSizeInKB = (file.size / 1024).toFixed(2);
    const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);

    setSelectedImage(file);
    setFileSize(fileSizeInMB < 1 ? `${fileSizeInKB} KB` : `${fileSizeInMB} MB`);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    validateAndSetImage(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    validateAndSetImage(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handlePreviewMessage = (e) => {
    e.preventDefault();
    setNotifPreview(true);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setFileSize(null);
  };

  const closePreview = () => {
    setNotifPreview(false);
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();

    const button = e.target;
    button.textContent = "Sending...";

    const token = getAuthToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const formData = new FormData();
    formData.append("message_header", messageHeader);
    formData.append("message", message);
    formData.append("location_id", notificationLocationId);
    if (selectedImage) {
      formData.append("image", selectedImage);
    }

    setLoading(true);

    try {
      const response = await axios.post(
        "/super-admin/create-app-notification",
        formData,
        config
      );
      setNotifPreview(false);
      setSendNotifSuccess(true);
      setMessageHeader("");
      setMessage("");
      setNotificationLocationId("");
      setSelectedImage(null);
      toast.success("Notification sent successfully!");
    } catch (error) {
      console.error("Error sending notification:", error);
      
      // Extract error message from response
      let errorMessage = "Failed to send notification. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      button.textContent = "Send Notification";
    }
  };

  const getInitials = (header) =>
    header
      .split(" ")
      .map((word) => word[0].toUpperCase())
      .join("")
      .slice(0, 2);

  return (
    <div className="dashboard container-fluid notif-dashboard">
      <div className="Nav">
        <Sidebar />
      </div>
      <div className="dashboard-display notif-container-display container-fluid">
        <div className="top-bar container-fluid">
          <div className="top-bar-info">
            <p id="date">{formattedDate}</p>
            <b>
              <p>Welcome back {adminData}</p>
            </b>
          </div>
        </div>
        <div className="notification-container container-fluid">
          <div className="show-notifications">
            <div className="notif-head">
              <h2>Notifications</h2>
            </div>
            <div className="notif-search">
              <input
                type="text"
                placeholder="Search fields"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                className="search-input"
              />
              <button className="search-btn" onClick={handleSearchClick}>
                Search
              </button>
            </div>
            <div className="notif-body">
              {loading ? (
                <div className="spinner" style={{ marginTop: "40%" }}></div>
              ) : displayNotifications.length > 0 ? (
                displayNotifications.map((notification, index) => (
                  <div key={index} className="notif">
                    <div className="initials-container">
                      <div
                        className="notif-initials"
                        style={{
                          backgroundColor: "#0473BD",
                          color: "white",
                          borderRadius: "50%",
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {getInitials(notification.message_header)}
                      </div>
                    </div>
                    <div className="notif-text">
                      <p style={{ fontWeight: "bolder", marginBottom: "8px" }}>
                        {notification.message_header}
                      </p>
                      <p>{notification.message}</p>

                      {notification.additional_data?.subscription_tnx_ref && (
                        <div className="notif-btn">
                          {notification.additional_data.status === "unpaid" && (
                            <div>
                              <button
                                style={{
                                  color: "#0473BD",
                                  marginRight: "10px",
                                }}
                                onClick={() =>
                                  notifApproveOpen(
                                    notification.additional_data
                                      .subscription_tnx_ref,
                                    notification.id
                                  )
                                }
                              >
                                Approve Payment
                              </button>
                              <button
                                style={{ color: "red" }}
                                onClick={() =>
                                  notifDeclineOpen(
                                    notification.additional_data
                                      .subscription_tnx_ref,
                                    notification.id
                                  )
                                }
                              >
                                Decline Payment
                              </button>
                            </div>
                          )}

                          {notification.additional_data.status === "paid" && (
                            <button style={{ color: "green" }}>Approved</button>
                          )}

                          {notification.additional_data.status ===
                            "declined" && (
                            <button style={{ color: "red" }}>Declined</button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="notif-time">
                      <p style={{ color: "#737373", fontWeight: "bold" }}>
                        {formatDistanceToNow(
                          parseISO(notification.created_at),
                          { addSuffix: true }
                        )}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="nil-notif">No notifications available.</p>
              )}
            </div>
            <div className="notif-footer">
              <div className="notif-pagination">
                <div className="notif-pagination-info">
                  Page {currentPage} of {totalPages || 1}
                </div>
                <div className="notif-pagination-controls">
                  <button 
                    className="notif-pagination-btn notif-pagination-prev"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </button>
                  <button 
                    className="notif-pagination-btn notif-pagination-next"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages || totalPages === 0 || loading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="send-notifications">
            <h3>Send Notification</h3>
            <p>Send a customized in-app message to Frebson users</p>
            <div className="send-notif-form">
              <form onSubmit={handlePreviewMessage}>
                {(currentAdminRoleId === 4 || currentAdminRoleId === 2) ? (
                  <input type="hidden" name="notificationLocation" value={notificationLocationId} />
                ) : (
                  <div className="notif-location">
                    <label htmlFor="notificationLocation">Gym Location</label>
                    <select
                      id="notificationLocation"
                      name="notificationLocation"
                      value={notificationLocationId}
                      onChange={(e) => setNotificationLocationId(e.target.value)}
                      required
                      disabled={locationsLoading}
                    >
                      <option value="" disabled>
                        {locationsLoading ? 'Loading locations...' : 'Select a location'}
                      </option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="notif-header">
                  <label htmlFor="messageHeader">Message Header</label>
                  <input
                    type="text"
                    id="messageHeader"
                    name="messageHeader"
                    value={messageHeader}
                    onChange={(e) => setMessageHeader(e.target.value)}
                    required
                  />
                </div>

                <div className="notif-message-image">
                  <h1>Upload Image (Optional)</h1>
                  {selectedImage ? (
                    <div className="image-upload-success">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/notif-img-upload.png`}
                        alt="upload placeholder"
                      />
                      <div className="upload-success-details">
                        <h1>Image uploaded successfully</h1>
                        <p>File size: {fileSize} </p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="image-input-container"
                      onClick={() =>
                        document.getElementById("imageInput").click()
                      }
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/images/notif-img-upload.png`}
                        alt="upload placeholder"
                      />
                      <h1>Click to upload and drag items</h1>
                      <p>jpeg and png.(Max 4mb)</p>
                    </div>
                  )}
                  <input
                    type="file"
                    id="imageInput"
                    accept="image/png, image/jpeg, application/pdf"
                    style={{ display: "none" }}
                    onChange={handleImageChange}
                  />
                </div>
                <div className="notif-message">
                  <label htmlFor="message">Enter Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Type a text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </div>
                <div className="notif-button">
                  <button type="submit">
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Components */}
      <NotificationPreviewModal
        isOpen={notifPreview}
        onRequestClose={closePreview}
        messageHeader={messageHeader}
        message={message}
        selectedImage={selectedImage}
        handleRemoveImage={handleRemoveImage}
        handleSendNotification={handleSendNotification}
        locationName={
          locations.find(loc => String(loc.id) === String(notificationLocationId))?.name || ""
        }
      />

      <NotificationSuccessModal
        isOpen={sendNotifSuccess}
        onRequestClose={() => setSendNotifSuccess(false)}
        title="Message Sent"
      />

      <PaymentApproveModal
        isOpen={notifApprove}
        onRequestClose={() => setNotifApprove(false)}
        paymentDetails={paymentDetails}
        onApprove={() => {
          setViewModal(true);
          setNotifApprove(false);
        }}
      />

      <PaymentDeclineModal
        isOpen={notifDecline}
        onRequestClose={() => setNotifDecline(false)}
        paymentDetails={paymentDetails}
        onDecline={() => {
          setViewModal2(true);
          setNotifDecline(false);
        }}
      />

      <PasswordVerificationModal
        isOpen={viewModal}
        onRequestClose={() => {
          setViewModal(false);
          setPassword(""); // Reset password when modal closes
        }}
        description="Enter password to approve payment"
        password={password}
        onPasswordChange={(e) => setPassword(e.target.value)}
        onSubmit={handleApprovePayment}
      />

      <PasswordVerificationModal
        isOpen={viewModal2}
        onRequestClose={() => {
          setViewModal2(false);
          setPassword(""); 
        }}
        description="Enter password to decline payment"
        password={password}
        onPasswordChange={(e) => setPassword(e.target.value)}
        onSubmit={handleDeclinePayment}
      />

      <NotificationSuccessModal
        isOpen={approveSuccess}
        onRequestClose={() => {
          setApproveSuccess(false);
          refreshNotifications(); 
        }}
        title="Payment Approved"
        shouldReload={false}
      />

      <NotificationSuccessModal
        isOpen={declineSuccess}
        onRequestClose={() => {
          setDeclineSuccess(false);
          refreshNotifications();
        }}
        title="Payment Declined"
        shouldReload={false}
      />
    </div>
  );
}

export default Notifications;
